(() => {
  const initialized = new WeakSet();

  function createCarouselController(carousel, runtime = window) {
    const track = carousel.querySelector('[data-fw-principle-track]');
    const cards = [...carousel.querySelectorAll('[data-fw-principle-card]')];
    const previous = carousel.querySelector('[data-fw-principle-prev]');
    const next = carousel.querySelector('[data-fw-principle-next]');
    const progress = carousel.querySelector('[data-fw-principle-progress]');
    const reducedMotion = runtime.matchMedia('(prefers-reduced-motion: reduce)');

    if (!track || !cards.length || !progress) return null;

    const format = (value) => String(value).padStart(2, '0');

    function cardOffset(card) {
      return card.offsetLeft - track.offsetLeft;
    }

    function activeIndex() {
      return cards.reduce((nearest, card, index) => {
        const currentDistance = Math.abs(cardOffset(card) - track.scrollLeft);
        const nearestDistance = Math.abs(cardOffset(cards[nearest]) - track.scrollLeft);
        return currentDistance < nearestDistance ? index : nearest;
      }, 0);
    }

    function updateProgress() {
      progress.textContent = `${format(activeIndex() + 1)} / ${format(cards.length)}`;
    }

    let requestedIndex = activeIndex();
    let scrollEndTimer;
    let programmaticScroll = false;

    function move(delta) {
      const destination = Math.min(
        cards.length - 1,
        Math.max(0, requestedIndex + delta),
      );
      if (destination === requestedIndex) return;

      requestedIndex = destination;
      programmaticScroll = true;
      track.scrollTo({
        left: cardOffset(cards[requestedIndex]),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    let animationFrame;
    track.addEventListener('scroll', () => {
      runtime.cancelAnimationFrame(animationFrame);
      animationFrame = runtime.requestAnimationFrame(updateProgress);
      if (!programmaticScroll) requestedIndex = activeIndex();
      runtime.clearTimeout(scrollEndTimer);
      scrollEndTimer = runtime.setTimeout(() => {
        requestedIndex = activeIndex();
        programmaticScroll = false;
      }, 120);
    }, { passive: true });

    function interruptProgrammaticScroll() {
      programmaticScroll = false;
      requestedIndex = activeIndex();
    }

    for (const eventName of ['pointerdown', 'touchstart', 'wheel']) {
      track.addEventListener(eventName, interruptProgrammaticScroll, { passive: true });
    }

    runtime.addEventListener?.('resize', () => {
      interruptProgrammaticScroll();
      updateProgress();
    });

    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(1);
      }
    });

    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    updateProgress();

    return {
      getRequestedIndex: () => requestedIndex,
      move,
      updateProgress,
    };
  }

  function initCarousel(carousel) {
    if (initialized.has(carousel)) return;

    const controller = createCarouselController(carousel);
    if (!controller) return;
    initialized.add(carousel);
  }

  function initCompactDetails(container) {
    if (initialized.has(container)) return;
    const details = [...container.querySelectorAll('details')];
    details.forEach((detail) => {
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;
        details.forEach((other) => {
          if (other !== detail) other.open = false;
        });
      });
    });
    initialized.add(container);
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-fw-principle-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-fw-compact-details]').forEach(initCompactDetails);
  }

  window.FormwynnPdpExperience = Object.assign(
    window.FormwynnPdpExperience || {},
    { createCarouselController },
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
